import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Building2, Bell, Shield, Database, Users, Palette, Upload, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import AccountSetupTab from '../../components/shared/AccountSetupTab';
import RolesPermissionsTab from '../../components/shared/RolesPermissionsTab';
import TeamsManager from '../../components/shared/TeamsManager';
import { useEnterprise } from '../../contexts/EnterpriseContext';

const RevQSettings = () => {
  const enterprise = useEnterprise();
  const [showConfirm, setShowConfirm] = useState(false);
  const [notifications, setNotifications] = useState({
    denialAlerts: true,
    arAlerts: true,
    complianceAlerts: true,
    weeklyReports: true,
    monthlyReports: true
  });

  const handleSave = () => {
    toast.success('Settings saved successfully');
  };

  return (
    <div className="space-y-6" data-testid="revq-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your revQ dashboard preferences</p>
      </div>

      <Tabs defaultValue="organization" className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="organization" className="gap-2"><Building2 className="h-4 w-4" /> Organization</TabsTrigger>
          <TabsTrigger value="notifications" className="gap-2"><Bell className="h-4 w-4" /> Notifications</TabsTrigger>
          <TabsTrigger value="users" className="gap-2"><Users className="h-4 w-4" /> Users</TabsTrigger>
          <TabsTrigger value="roles" className="gap-2" data-testid="tab-roles"><Shield className="h-4 w-4" /> Roles</TabsTrigger>
          <TabsTrigger value="teams" className="gap-2" data-testid="tab-teams"><Users className="h-4 w-4" /> Teams</TabsTrigger>
          <TabsTrigger value="setup" className="gap-2" data-testid="tab-setup"><Upload className="h-4 w-4" /> Account Setup</TabsTrigger>
          <TabsTrigger value="display" className="gap-2"><Palette className="h-4 w-4" /> Display</TabsTrigger>
        </TabsList>

        {/* Organization Settings */}
        <TabsContent value="organization">
          <Card>
            <CardHeader>
              <CardTitle>Organization Details</CardTitle>
              <CardDescription>Basic information about your practice</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="org-name">Organization Name</Label>
                  <Input id="org-name" defaultValue="Thriving Medical Group" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tax-id">Tax ID</Label>
                  <Input id="tax-id" defaultValue="XX-XXXXXXX" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="npi">NPI</Label>
                  <Input id="npi" defaultValue="1234567890" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="specialty">Primary Specialty</Label>
                  <Select defaultValue="multispecialty">
                    <SelectTrigger id="specialty">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="multispecialty">Multi-Specialty</SelectItem>
                      <SelectItem value="internal">Internal Medicine</SelectItem>
                      <SelectItem value="family">Family Medicine</SelectItem>
                      <SelectItem value="cardiology">Cardiology</SelectItem>
                      <SelectItem value="orthopedics">Orthopedics</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={handleSave} className="bg-black hover:bg-gray-800">Save Changes</Button>
            </CardContent>
          </Card>

          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Practice Type</CardTitle>
              <CardDescription>Choose your practice configuration to unlock the right features</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4" data-testid="practice-type-selector">
                <button
                  onClick={() => {
                    if (enterprise?.isEnterprise) {
                      enterprise.setEnterprise(false);
                      toast.success('Switched to Solo / Small practice mode');
                    }
                  }}
                  style={{
                    padding: 16, borderRadius: 10, border: `1.5px solid ${!enterprise?.isEnterprise ? '#111' : '#e0e0e0'}`,
                    background: !enterprise?.isEnterprise ? '#f9fafb' : '#fff', cursor: 'pointer', textAlign: 'left',
                  }}
                  data-testid="practice-type-solo">
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#111', margin: 0 }}>Solo / Small practice</p>
                  <p style={{ fontSize: 11, color: '#888', margin: '4px 0 0' }}>1&ndash;5 providers, generalist billers, standard Billing Focus</p>
                </button>
                <button
                  onClick={() => {
                    if (!enterprise?.isEnterprise) setShowConfirm(true);
                  }}
                  style={{
                    padding: 16, borderRadius: 10, border: `1.5px solid ${enterprise?.isEnterprise ? '#111' : '#e0e0e0'}`,
                    background: enterprise?.isEnterprise ? '#f9fafb' : '#fff', cursor: 'pointer', textAlign: 'left',
                  }}
                  data-testid="practice-type-enterprise">
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#111', margin: 0 }}>Multi-provider / Enterprise</p>
                  <p style={{ fontSize: 11, color: '#888', margin: '4px 0 0' }}>6+ providers, dedicated billing roles, team features</p>
                </button>
              </div>
              {showConfirm && (
                <div style={{ marginTop: 16, padding: 14, background: '#fffbeb', border: '0.5px solid #fde68a', borderRadius: 10 }}
                  data-testid="enterprise-confirm">
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <AlertTriangle style={{ width: 16, height: 16, color: '#d97706', marginTop: 1, flexShrink: 0 }} />
                    <div>
                      <p style={{ fontSize: 12, color: '#92400e', fontWeight: 500, margin: 0 }}>Enabling Enterprise mode adds team roles, worklist assignment, and supervisor reporting. This affects all users in your practice. Continue?</p>
                      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                        <button onClick={() => { enterprise?.setEnterprise(true); setShowConfirm(false); toast.success('Enterprise mode enabled'); }}
                          style={{ height: 32, padding: '0 14px', background: '#6b5ce7', color: '#fff', borderRadius: 7, fontSize: 12, fontWeight: 500, border: 'none', cursor: 'pointer' }}
                          data-testid="enable-enterprise-btn">
                          Enable Enterprise mode
                        </button>
                        <button onClick={() => setShowConfirm(false)}
                          style={{ height: 32, padding: '0 14px', border: '0.5px solid #e0e0e0', borderRadius: 7, fontSize: 12, color: '#555', background: '#fff', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Locations</CardTitle>
              <CardDescription>Practice locations for reporting</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {['Main Clinic - 123 Healthcare Ave', 'East Branch - 456 Medical Dr', 'West Branch - 789 Wellness Blvd'].map((location, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span>{location}</span>
                    <Button variant="ghost" size="sm">Edit</Button>
                  </div>
                ))}
                <Button variant="outline" className="w-full">+ Add Location</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Settings */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>Alert Preferences</CardTitle>
              <CardDescription>Configure when you receive notifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Denial Alerts</p>
                  <p className="text-sm text-muted-foreground">Get notified when denial rate exceeds threshold</p>
                </div>
                <Switch 
                  checked={notifications.denialAlerts} 
                  onCheckedChange={(v) => setNotifications({...notifications, denialAlerts: v})} 
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">A/R Aging Alerts</p>
                  <p className="text-sm text-muted-foreground">Alert when accounts exceed 90 days</p>
                </div>
                <Switch 
                  checked={notifications.arAlerts} 
                  onCheckedChange={(v) => setNotifications({...notifications, arAlerts: v})} 
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Compliance Alerts</p>
                  <p className="text-sm text-muted-foreground">Notify on coding errors or compliance issues</p>
                </div>
                <Switch 
                  checked={notifications.complianceAlerts} 
                  onCheckedChange={(v) => setNotifications({...notifications, complianceAlerts: v})} 
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Weekly Reports</p>
                  <p className="text-sm text-muted-foreground">Receive weekly summary every Monday</p>
                </div>
                <Switch 
                  checked={notifications.weeklyReports} 
                  onCheckedChange={(v) => setNotifications({...notifications, weeklyReports: v})} 
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Monthly Reports</p>
                  <p className="text-sm text-muted-foreground">Receive monthly financial summary</p>
                </div>
                <Switch 
                  checked={notifications.monthlyReports} 
                  onCheckedChange={(v) => setNotifications({...notifications, monthlyReports: v})} 
                />
              </div>
              <Button onClick={handleSave} className="bg-black hover:bg-gray-800">Save Preferences</Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Settings */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle>Team Members</CardTitle>
              <CardDescription>Manage dashboard access</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { name: 'Admin User', email: 'admin@clinic.com', role: 'Administrator' },
                  { name: 'Billing Manager', email: 'billing@clinic.com', role: 'Manager' },
                  { name: 'Staff Biller', email: 'biller@clinic.com', role: 'Biller' },
                ].map((user, i) => (
                  <div key={i} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">{user.name}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm bg-slate-200 px-2 py-1 rounded">{user.role}</span>
                      <Button variant="ghost" size="sm">Edit</Button>
                    </div>
                  </div>
                ))}
                <Button variant="outline" className="w-full">+ Invite User</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Roles & Permissions */}
        <TabsContent value="roles">
          <RolesPermissionsTab appKey="revq" />
        </TabsContent>

        {/* Teams */}
        <TabsContent value="teams">
          <TeamsManager />
        </TabsContent>

        {/* Account Setup */}
        <TabsContent value="setup">
          <AccountSetupTab appKey="revq" />
        </TabsContent>

        {/* Display Settings */}
        <TabsContent value="display">
          <Card>
            <CardHeader>
              <CardTitle>Display Preferences</CardTitle>
              <CardDescription>Customize your dashboard appearance</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Default Dashboard View</Label>
                <Select defaultValue="manager">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manager">Executive View</SelectItem>
                    <SelectItem value="biller">Biller View</SelectItem>
                    <SelectItem value="physician">Provider View</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Currency Format</Label>
                <Select defaultValue="usd">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="usd">USD ($)</SelectItem>
                    <SelectItem value="eur">EUR (€)</SelectItem>
                    <SelectItem value="gbp">GBP (£)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date Format</Label>
                <Select defaultValue="mdy">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mdy">MM/DD/YYYY</SelectItem>
                    <SelectItem value="dmy">DD/MM/YYYY</SelectItem>
                    <SelectItem value="ymd">YYYY-MM-DD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleSave} className="bg-black hover:bg-gray-800">Save Preferences</Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RevQSettings;
